import type { Material, Object3D } from "three";
import { Action } from "../../../base/common/actions.js";
import { Attachment } from "../../../base/common/attachment.js";
import { getSourceCode } from "../../../base/common/coding.js";
import { CommandService } from "../../commands/common/commandService.js";
import { CommandsRegistry } from "../../commands/common/commands.js";
import type { IPropertyGroup } from "../../properties/common/properties.js";
import type { ILicenseService } from "../../auth/common/licenses.js";
import type { ThreeJs } from "./webgl.js";
import type { ThreePropertyDecorations } from "./propertyReader.js";
import { isRendererWithBackend } from "./helper.js";
import { isNodeMaterial } from "./helper.nodes.js";
import { tslMaterialToGraph, expandFnCall, type FnBuildContext } from "./nodegraph.tsl.js";
import { emitMaterialTsl, hasTslCode, type FnExpander } from "./nodegraph.tsl.code.js";

    /** True when the material exposes a non-empty shader-source string for `key`
     *  (ShaderMaterial/RawShaderMaterial do; built-in materials don't). */
    function hasReadableShader(material: Material, key: "vertexShader" | "fragmentShader"): boolean {
        const source = (material as unknown as Record<string, unknown>)[key];
        return typeof source === "string" && source.trim().length > 0;
    }

    // #region Compiled shader source
    // The FINAL code the renderer runs — for node/TSL materials via three's own
    // debug API (`renderer.debug.getShaderAsync`, reading the retained
    // NodeBuilderState: WGSL on the WebGPU backend, generated GLSL on its WebGL
    // backend), for classic WebGLRenderer materials via `gl.getShaderSource` on
    // the compiled program. Both read the OWNING renderer (never a second one —
    // see findRendererForMaterial) and never write back.

    /** Cheap availability probe (no compile, no side effects): resolvable owning
     *  renderer + a readable compiled path for it. */
    /** Builds an anonymous-Fn expander for the material's reconstructed TSL, or
     *  null when no node render context is resolvable. Mirrors the compiled-shader
     *  context resolution: the owning renderer/scene (+ camera) that `expandFnCall`
     *  needs to run the Fn closures. Node backend only — classic WebGL materials
     *  have no TSL Fns to expand. */
    function resolveFnExpander(engine: ThreeJs | undefined, material: Material): FnExpander | null {
        try {
            const owner = engine?.findRendererForMaterial(material);
            if (!owner?.renderer || !owner.scene || !isRendererWithBackend(owner.renderer)) return null;
            const ctx: FnBuildContext = {
                renderer: owner.renderer,
                scene: owner.scene,
                camera: engine?.getCamera() ?? undefined,
                material,
            };
            return (fnCallNode) => expandFnCall(fnCallNode, ctx);
        } catch {
            return null;
        }
    }

    function canReadCompiledShader(engine: ThreeJs | undefined, material: Material): boolean {
        try {
            const owner = engine?.findRendererForMaterial(material);
            if (!owner?.renderer) return false;
            const renderer = owner.renderer as unknown as Record<string, any>;
            if (isRendererWithBackend(owner.renderer)) {
                return typeof renderer.debug?.getShaderAsync === "function"
                    && !!owner.scene && !!owner.object && !!engine?.getCamera();
            }
            // Classic WebGL: a program exists once the material rendered. has()
            // first — WebGLProperties.get() CREATES entries for unknown objects.
            const props = renderer.properties as { has?(o: object): boolean; get(o: object): { currentProgram?: unknown } } | undefined;
            if (!props || typeof props.has !== "function" || !props.has(material)) return false;
            return !!props.get(material)?.currentProgram;
        } catch {
            return false;
        }
    }

    /** Reads the compiled shader for one stage, or null. Guarded: runs the
     *  inspected page's renderer code (getShaderAsync awaits a compile — a cache
     *  hit when the material is on screen). */
    async function getCompiledShader(engine: ThreeJs | undefined, material: Material, stage: "vertex" | "fragment"): Promise<string | null> {
        try {
            const owner = engine?.findRendererForMaterial(material);
            if (!owner?.renderer) return null;
            const renderer = owner.renderer as unknown as Record<string, any>;
            if (isRendererWithBackend(owner.renderer)) {
                const camera = engine?.getCamera();
                if (!owner.scene || !owner.object || !camera) return null;
                const result = await renderer.debug.getShaderAsync(owner.scene, camera, owner.object);
                const source = stage === "vertex" ? result?.vertexShader : result?.fragmentShader;
                return typeof source === "string" && source.length ? source : null;
            }
            const program = (renderer.properties as { get(o: object): { currentProgram?: { vertexShader?: WebGLShader; fragmentShader?: WebGLShader } } })
                .get(material)?.currentProgram;
            const gl = renderer.getContext?.() as WebGLRenderingContext | undefined;
            const shader = stage === "vertex" ? program?.vertexShader : program?.fragmentShader;
            if (!gl || !shader) return null;
            return gl.getShaderSource(shader) ?? null;
        } catch {
            return null;
        }
    }
    // #endregion

export class EditorPropertyDecorations implements ThreePropertyDecorations {
    constructor(private readonly engine: ThreeJs, private readonly license: ILicenseService) {}
    object(group: IPropertyGroup, object: object, name: string): void {
        group.actions.push(
            new Action("log-object", "Console.Log to browser console", true, async () => { console.log(`Logging object: ${name}`, object); }),
            new Action("get-sourcecode", "View Code", true, async () => getSourceCode(object)),
        );
    }
    component(group: IPropertyGroup, object: Object3D, component: object, name: string, removable: boolean): void {
        this.object(group, component, name);
        if (removable && CommandsRegistry.getCommand("needle.component.remove")) {
            group.actions.push(new Action("remove-component", "Remove Component", true, async () => {
                await CommandService.get().executeCommand("needle.component.remove", { object, component });
            }));
        }
    }
    docs(group: IPropertyGroup, url: string): void {
        group.attachments.push(new Attachment("open-docs-" + group.name, "", "Open documentation for " + group.name, "icon-docs", async () => {
            CommandService.get().executeCommand("editor.action.url.openUrl", url);
        }));
    }
    material(group: IPropertyGroup, material: Material): void {
        group.actions.push(...[
                new Action("log-object", "Console.Log to browser console", true, async () => {
                    console.log(`Logging object: ${material.name} (${material.type})`, material);
                }),
                new Action("get-sourcecode", "View Code", true, async () => {
                    return getSourceCode(material);
                }),
                // Enabled only when the shader is actually READABLE (a non-empty
                // string): built-in materials compile their shaders internally and
                // expose nothing — a mere `"vertexShader" in material` check offered
                // dead entries in the code panel's source dropdown.
                new Action("get-sourcecode", "View Vertex Shader", hasReadableShader(material, "vertexShader"), async () => {
                    return (material as any).vertexShader;
                }),
                new Action("get-sourcecode", "View Fragment Shader", hasReadableShader(material, "fragmentShader"), async () => {
                    return (material as any).fragmentShader;
                }),
                // The FINAL code the GPU runs (TSL/node materials compile to WGSL
                // or GLSL; built-ins to GLSL) — see #Compiled shader source.
                new Action("get-sourcecode", "View Compiled Vertex Shader", canReadCompiledShader(this.engine, material), async () => {
                    return getCompiledShader(this.engine, material, "vertex");
                }),
                new Action("get-sourcecode", "View Compiled Fragment Shader", canReadCompiledShader(this.engine, material), async () => {
                    return getCompiledShader(this.engine, material, "fragment");
                }),
                // Reconstructed TSL source of the material's node graph (see
                // nodegraph.tsl.code.ts) — only for materials with node roots.
                new Action("get-sourcecode", "View TSL Code", hasTslCode(material), async () => {
                    // Expanding anonymous Fn bodies runs the page's closures and
                    // is the Pro "going deeper" payoff — free tier keeps the stub.
                    // Only build the expander when licensed AND a node context is
                    // resolvable (WebGPU/node backend on screen).
                    const expandFn = this.license?.isInspectorAccessAllowed === true
                        ? resolveFnExpander(this.engine, material) ?? undefined
                        : undefined;
                    return emitMaterialTsl(material, expandFn);
                }),
                new Action("view-nodegraph", "View Node Graph", isNodeMaterial(material), async () => {
                    return tslMaterialToGraph(material);
                }),
            ]);
        const url = (material as Material & { isMeshStandardMaterial?: boolean }).isMeshStandardMaterial && material.type === "StandardMaterial"
            ? "https://threejs.org/docs/#api/en/materials/MeshStandardMaterial"
            : `https://threejs.org/docs/#api/en/materials/${material.type}`;
        this.docs(group, url);
    }
}
